import 'react-circular-progressbar/dist/styles.css';
import { APP_STYLE_CONFIG } from '@/constants/config';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import { cn } from '@/utils/cn';
import { PassFailStartCap } from './subcomponents/PassFailStartCap';
import { PassFailThreshold } from './subcomponents/PassFailThreshold';
import { ProgressProvider } from './subcomponents/ProgressProvider';
import { RadialSeparators } from './subcomponents/RadialSeparators';
import { getRoleplayVisibleSeparatorIndices, SCORE_CIRCLE_DEFAULTS } from './config';
import { RoleplayGradientArc } from './subcomponents/RolePlayGradientArc';
import { ScoreReadout } from './subcomponents/ScoreReadout';
import { useScoreCircle } from './utils/useScoreCircle';
import React from 'react';
import type { ScoreCircleProps } from './types';

/**
 * ScoreCircle is a React component that visualizes a numeric score as a
 * circular progress bar. It supports two variants: "pass/fail" mode, which
 * uses a single color and a passing threshold marker; and "role-play" mode,
 * which uses a segmented gradient arc to indicate score bands. The component
 * is highly customizable via props for colors, animation, and layout.
 *
 * @requires react-circular-progressbar for the base circular progress rendering.
 * @requires AnimatedNumber for animating the numeric score display.
 */
export const ScoreCircle: React.FC<ScoreCircleProps> = ({
  circleRatio = SCORE_CIRCLE_DEFAULTS.circleRatio,
  className,
  isLoading = false,
  isPrinting = false,
  passingThreshold = SCORE_CIRCLE_DEFAULTS.passingThreshold,
  rotation = SCORE_CIRCLE_DEFAULTS.rotation,
  runtimeRatingCutoffs,
  simulationType = SCORE_CIRCLE_DEFAULTS.simulationType,
  useAnimatedNumbers = APP_STYLE_CONFIG.useAnimatedNumbers,
  useRolePlayGradient = SCORE_CIRCLE_DEFAULTS.useRolePlayGradient,
  value = SCORE_CIRCLE_DEFAULTS.value,
  valueMax = SCORE_CIRCLE_DEFAULTS.valueMax,
}) => {
  const {
    atPassingThreshold,
    colors,
    normalizedValue,
    normalizedValueMax,
    numSeparators,
    numSeparatorsForPassingLine,
    passingLineHeight,
    passingLineMarginTop,
    passingLineTurn,
    skillLevel,
    strokeWidth,
    styles,
    variant,
  } = useScoreCircle({
    circleRatio,
    isLoading,
    passingThreshold,
    rotation,
    runtimeRatingCutoffs,
    simulationType,
    useRolePlayGradient,
    value,
    valueMax,
  });

  return (
    <div className={cn('relative h-[211px] w-[250px]', className)}>
      <ProgressProvider valueStart={0} valueEnd={normalizedValue}>
        {(progressProviderValue) => (
          <CircularProgressbarWithChildren
            circleRatio={circleRatio}
            className="relative"
            maxValue={normalizedValueMax}
            strokeWidth={strokeWidth}
            styles={styles}
            value={isLoading ? 0 : progressProviderValue}
          >
            {variant === 'pass/fail' && atPassingThreshold && (
              <PassFailStartCap
                color={colors.pathColor}
                rotation={rotation}
                strokeWidth={strokeWidth}
              />
            )}

            {variant === 'role-play' && useRolePlayGradient && (
              <RoleplayGradientArc
                circleRatio={circleRatio}
                flatEndCap={getRoleplayVisibleSeparatorIndices(runtimeRatingCutoffs).has(value)}
                isLoading={isLoading}
                progressValue={progressProviderValue}
                runtimeRatingCutoffs={runtimeRatingCutoffs}
                rotation={rotation}
                scoreValue={progressProviderValue}
                strokeWidth={strokeWidth}
                valueMax={normalizedValueMax}
              />
            )}

            <ScoreReadout
              isLoading={isLoading}
              isPrinting={isPrinting}
              progressProviderValue={progressProviderValue}
              skillLevel={skillLevel ? skillLevel : undefined}
              simulationType={simulationType}
              useAnimatedNumbers={useAnimatedNumbers}
              value={normalizedValue}
            />

            {variant === 'pass/fail' && (
              <PassFailThreshold
                circleRatio={circleRatio}
                numSeparatorsForPassingLine={numSeparatorsForPassingLine}
                passingLineHeight={passingLineHeight}
                passingLineMarginTop={passingLineMarginTop}
                passingLineTurn={passingLineTurn}
                passingThreshold={passingThreshold}
                rotation={rotation}
              />
            )}

            <RadialSeparators
              circleRatio={circleRatio}
              indexMax={passingThreshold}
              numSeparators={numSeparators}
              rotation={rotation}
              runtimeRatingCutoffs={runtimeRatingCutoffs}
              value={isLoading ? 0 : progressProviderValue}
              variant={variant}
            />
          </CircularProgressbarWithChildren>
        )}
      </ProgressProvider>
    </div>
  );
};

ScoreCircle.displayName = 'ScoreCircle';

export default ScoreCircle;
